import { Suspense } from 'react';
import { AnimatePresence } from 'framer-motion';
import { MapView } from '@/map/MapView';
import { useAppStore } from '@/stores/appStore';
import { useKeyboardControls } from '@/hooks/useKeyboardControls';
import { useKioskMode } from '@/hooks/useKioskMode';
import { ScenarioButton } from '@/components/ScenarioButton';
import { ScenarioNotification } from '@/components/ScenarioNotification';
import { ProductScreenOverlay } from '@/components/ProductScreenOverlay';
import { ScenarioControls } from '@/components/ScenarioControls';
import { WelcomeScreen } from '@/components/WelcomeScreen';
import { OperatorControls } from '@/components/OperatorControls';
import { ScenarioHUD } from '@/components/ScenarioHUD';
import { ScenarioDataPanel } from '@/components/ScenarioDataPanel';
import { StyleSwitcher } from '@/components/StyleSwitcher';
import { ColorGrade } from '@/components/ColorGrade';
import { panelRegistry } from '@/panels/index';

export default function App() {
  const { kioskMode } = useAppStore((s) => s.settings);
  const activePanel = useAppStore((s) => s.uiState.activePanel);
  const closePanel = useAppStore((s) => s.closePanel);
  const { isIdle, resetIdle } = useKioskMode();
  useKeyboardControls();

  const PanelComponent = activePanel
    ? (panelRegistry[activePanel] as React.ComponentType<any>)
    : null;

  return (
    <div className="w-screen h-screen overflow-hidden relative" style={{ backgroundColor: 'var(--panel-bg)' }}>
      {/* Mapbox 3D Map - Full viewport */}
      <MapView />
      <StyleSwitcher />

      {/* Futuristic overlay effects */}
      <div className="scan-line" />
      <div className="film-grain" />
      <div className="vignette" />
      <div className="weather-overlay" />
      <ColorGrade />

      {/* UI Overlay Layer */}
      <div className="absolute inset-0 pointer-events-none z-10">
        {/* Scenario HUD - left panel during playback */}
        <ScenarioHUD />

        {/* Scenario Data Panel - right panel during playback */}
        <ScenarioDataPanel />

        {/* Scenario Trigger Button - bottom center */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 pointer-events-auto">
          <ScenarioButton />
        </div>

        {/* Scenario Notification */}
        <ScenarioNotification />
        <ProductScreenOverlay />
        <ScenarioControls />

        {/* Right Panel */}
        <AnimatePresence>
          {PanelComponent && (
            <div className="absolute top-0 right-0 h-full w-[480px] pointer-events-auto">
              <Suspense
                fallback={
                  <div className="h-full glass-panel animate-pulse" />
                }
              >
                <PanelComponent onClose={closePanel} />
              </Suspense>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Kiosk Welcome */}
      {kioskMode && isIdle && <WelcomeScreen onStart={resetIdle} />}

      {/* Hidden Operator Controls */}
      <OperatorControls />
    </div>
  );
}
